import * as e  from 'emerge'
import * as a from '@mitranim/js/all.mjs'
import * as ob from '@mitranim/js/obs.mjs'
import * as n  from './net.mjs'
import * as c  from './conf.mjs'
import * as um from './util-misc.mjs'
import * as uc from './util-coll.mjs'
import * as ut from './util-text.mjs'
import * as ud from './util-dom.mjs'
import * as uma from './util-math.mjs'
import * as uti from './util-time.mjs'
import * as ue from './util-err.mjs'

/*
Encapsulates state and methods for supporting forms in UI.

Lifecycle of data around a form:

  ┌ -> entity -> fields -> request body -> response body = entity ->- ┐
  └ -<-------------------------------------------------------------<- ┘

The form is an observable that contains fields and additional metadata computed
from fields. See `Form..reinitMeta` for available metadata.

Fields are accessed via `form.fields`.

See `Form..makeField` for the known field definitions.

Initializing a form:

  const formSpec = {
    fieldSpecs: [
      {name: 'someField',  type: 'text',  validators: [validateFieldNotEmpty]},
      {name: 'otherField', type: 'image', validators: [validateFieldImageFileNotEmpty]},
    ],
  }
  const form = new u.Form(formSpec)
  form.resetFromEntity(ent)

Data for HTTP requests:

  await form.invalidate()
  const body = await form.toEntity(ctx)

Data for URL query:

  await form.invalidate()
  const query = form.toQuery()

Updating on entity changes:

  form.refreshFromEntity(ent)

Updating on URL query changes:

  form.resetFromUrl(url)

Clearing a form completely:

  form.resetFromEntity()

Usage in a view:

  render() {
    const {fields, hasErrors, hasValues, someTouched, allTouched} = form
    return <input {...fields.someField.linkInput()} />
  }

Mapping over fields:

  render() {
    return form.enabledFields().map(field => <m.FormFieldAny field={field} />)
  }
*/
export class Form extends ob.Obs {
  constructor(spec) {
    super()

    this.spec = reqFormSpec(spec)
    this.updates = 0
    this.updateQuery = undefined

    this.reinitFields()
    this.recompute()
    this.reinitMeta()
  }

  reinitFields() {
    this.fields = a.Emp()
    this.fieldList = []

    for (const spec of a.arr(this.spec.fieldSpecs)) {
      this.fieldList.push(
        this.fields[a.reqKey(spec.name)] = this.makeField(spec),
      )
    }

    /*
    Convention supported by some form specs. Deprecated. Instead, when
    generating field specs, we should include `fieldSpec.value` for each field,
    reading the value from the entity.
    */
    const {entity} = this.spec
    if (entity) this.resetFromEntity(entity)
  }

  reinitMeta() {
    const fields = this.filterFields(isFieldEnabled)
    this.hasErrors = a.some(fields, fieldError)
    this.hasValues = a.some(fields, fieldHasValue)
    this.someTouched = a.some(fields, isFieldTouched)
    this.allTouched = a.every(fields, isFieldTouched)
  }

  recompute() {
    const MAX_RECOMPUTATIONS = 8

    let ind = -1
    while (++ind < MAX_RECOMPUTATIONS) {
      const {updates} = this
      for (const field of this.fieldList) field.recompute()
      if (a.is(updates, this.updates)) return
    }

    throw Error(`possible infinite loop: fields recomputed more than ${MAX_RECOMPUTATIONS} times`)
  }

  validate() {for (const field of this.fieldList) field.validate()}
  forceValidate() {for (const field of this.fieldList) field.forceValidate()}
  onFieldChange() {this.synced(this.validate)}

  // Unused for now.
  // reqGroups() {for (const {spec} of this.fieldList) reqFieldSpecGroup(spec)}

  synced(fun) {
    a.reqFun(fun)

    if (this.updates) {
      this.updates++
      return
    }

    ob.Sched.main.pause()
    this.updates = 1

    try {
      fun.call(this, this)
      this.recompute()
      this.reinitMeta()
      this.trigger()

      // Tentative. May make this optional, call only on field changes,
      // and avoid calling when resetting or refreshing the form.
      this.updateQueryOpt()
    }
    finally {
      this.updates = 0
      ob.Sched.main.resume()
    }
  }

  /*
  Triggers any subscribers watching the form. Must be called manually because
  fields are not observable, and their mutations don't automatically trigger
  the form. This is by design. Non-observable fields make recomputation easier
  and safer.
  */
  trigger() {ob.ph(this).obs.trig()}

  /*
  Resets every field to the values derived from the entity. Fields can override
  the default behavior by implementing the `.resetFromEntity` method.
  */
  resetFromEntity(src) {
    src = this.fromEntity(src)
    this.synced(function formUpdate(form) {
      for (const field of form.fieldList) field.resetFromEntity(src)
    })
  }

  // Same as `.resetFromEntity` but affects only untouched fields.
  refreshFromEntity(src) {
    src = this.fromEntity(src)
    this.synced(function formUpdate(form) {
      for (const field of form.fieldList) if (!field.touched) field.resetFromEntity(src)
    })
  }

  /*
  Finds fields that implement `.resetFromUrl` and resets them to the values in
  the given URL's query. Not every field type can be meaningfully encoded and
  decoded through the query; for example, it's not possible for composite image
  fields. Fields that don't implement query encoding/decoding are left as-is.
  */
  resetFromUrl(src) {
    src = ut.toUrl(src)
    this.synced(function formUpdate(form) {
      for (const field of form.fieldList) field.resetFromUrl(src)
    })
  }

  /*
  Same as `.resetFromUrl` but affects only untouched fields, similar to the
  difference between `.resetFromEntity` and `.refreshFromEntity`.
  */
  refreshFromUrl(src) {
    src = ut.toUrl(src)
    this.synced(function formUpdate(form) {
      for (const field of form.fieldList) if (!field.touched) field.resetFromUrl(src)
    })
  }

  /*
  Subclasses may override this to modify the set of available fields. Should be
  paired with a custom `.toEntity`. For example, when editing services, we
  replace `duration` (seconds) with `durationMinutes`.
  */
  fromEntity(src) {return a.laxStruct(src)}

  // Generates a dict suitable for JSON API.
  async toEntity(ctx) {
    return um.dotNest(um.combine(await um.PromiseList.map(this.fieldList, function fieldToEntityPatch(val) {
      return val.getEntityPatch(ctx)
    })))
  }

  // Generates a dict of strings suitable for the URL query.
  toQuery() {return um.combine(this.mapFields(fieldQueryPatch))}

  mapFields(fun) {return a.map(this.fieldList, fun)}
  filterFields(fun) {return a.filter(this.fieldList, fun)}
  enabledFields() {return this.filterFields(isFieldEnabled)}

  eachField(fun) {
    a.reqFun(fun)
    ob.Sched.main.pause()
    try {for (const val of this.fieldList) fun(val)}
    finally {ob.Sched.main.resume()}
  }

  /*
  Allows to propagate field updates to URL query. Example usage:

    form.linkQuery(ctx.env.locPushQuery.bind(ctx.env))

  Note: this only links in ONE DIRECTION: from the fields to the query.
  Updates from the query to the fields must be propagated back by calling
  `form.resetFromUrl` or `form.refreshFromUrl`.
  */
  linkQuery(fun) {this.updateQuery = a.optFun(fun)}
  linkQueryOpt(env) {if (c.DOM_AVAILABLE) this.linkQuery(env.locPushPatchQuery.bind(env))}
  updateQueryOpt() {this.updateQuery?.(this.toQuery())}

  /*
  Validates the form. Then, if there are no errors, returns void. Otherwise
  scrolls to the first errored field and throws an error with the field name
  and error text.
  */
  async invalidate() {
    this.forceValidate()
    const field = this.firstFailedField()
    if (!field) return

    const name = field.getTitle()
    const err = field.error

    field.scrollToVisible()
    throw Error(name ? `${a.show(name)}: ${err}` : err)
  }

  firstFailedField() {return a.find(this.fieldList, fieldError)}
  getField(key) {return this.fields[a.reqKey(key)]}
  clear() {this.synced(this.clearUnsync)}
  clearUnsync() {this.eachField(fieldClear)}
  untouch() {this.synced(this.untouchUnsync)}
  untouchUnsync() {this.eachField(fieldUntouch)}
  fieldGroups() {return a.entries(a.group(this.enabledFields(), fieldGroupTitle))}
  avgRating() {return uma.avg(this.filterFields(isFilledRatingField).map(fieldValueSingle))}

  makeField(spec) {
    const {type} = reqFieldSpec(spec)

    if (type === c.UI_FIELD_TYPE_HIDDEN)            return new GenericField(this, spec)
    if (type === c.UI_FIELD_TYPE_TEXT_WITH_RADIO)   return new TextField(this, spec)
    if (type === c.UI_FIELD_TYPE_TEXTAREA)          return new TextareaField(this, spec)
    if (type === c.UI_FIELD_TYPE_TEXT_MD)           return new TextareaField(this, spec)
    if (type === c.UI_FIELD_TYPE_EMAIL)             return new EmailField(this, spec)
    if (type === c.UI_FIELD_TYPE_PASSWORD)          return new PasswordField(this, spec)
    if (type === c.UI_FIELD_TYPE_PHONE)             return new PhoneField(this, spec)
    if (type === c.UI_FIELD_TYPE_NUMBER)            return new NumberField(this, spec)
    if (type === c.UI_FIELD_TYPE_NUMBER_WITH_RADIO) return new NumberField(this, spec)
    if (type === c.UI_FIELD_TYPE_CENTS)             return new CentsField(this, spec)
    if (type === c.UI_FIELD_TYPE_CENTS_WITH_RADIO)  return new CentsField(this, spec)
    if (type === c.UI_FIELD_TYPE_BOOL)              return new BoolField(this, spec)
    if (type === c.UI_FIELD_TYPE_CHECKBOX)          return new BoolField(this, spec)
    if (type === c.UI_FIELD_TYPE_SWITCH)            return new BoolField(this, spec)
    if (type === c.UI_FIELD_TYPE_FILE)              return new FileField(this, spec)
    if (type === c.UI_FIELD_TYPE_AVATAR_FILE)       return new ImageFileField(this, spec)
    if (type === c.UI_FIELD_TYPE_IMAGE_FILE)        return new ImageFileField(this, spec)
    if (type === c.UI_FIELD_TYPE_VIDEO_FILE)        return new VidField(this, spec)
    if (type === c.UI_FIELD_TYPE_BOOK_FILE)         return new BookFileField(this, spec)
    if (type === c.UI_FIELD_TYPE_DATE)              return new DateField(this, spec)
    if (type === c.UI_FIELD_TYPE_DATETIME)          return new DateTimeField(this, spec)
    if (type === c.UI_FIELD_TYPE_GEO_NAME)          return new GeoFieldPoint(this, spec)
    if (type === c.UI_FIELD_TYPE_GOOGLE_PLACE_NAME) return new GeoFieldGooglePlaceId(this, spec)
    if (type === c.UI_FIELD_TYPE_SELECT)            return new SelectField(this, spec)
    if (type === c.UI_FIELD_TYPE_DROPDOWN)          return new SelectField(this, spec)
    if (type === c.UI_FIELD_TYPE_RADIO)             return new SelectField(this, spec)
    if (type === c.UI_FIELD_TYPE_INPUT_LADDER)      return new SelectField(this, spec)
    if (type === c.UI_FIELD_TYPE_SELECT_LADDER)     return new SelectField(this, spec)
    if (type === c.UI_FIELD_TYPE_STAR_RATING)       return new StarRatingField(this, spec)
    if (type === c.UI_FIELD_TYPE_RATE_PENTA)        return new RatePentaField(this, spec)
    if (type === c.UI_FIELD_TYPE_RATE_DEC)          return new RateDecField(this, spec)
    if (isFieldTypeText(type))                      return new TextField(this, spec)
    throw Error(`unrecognized field type ${a.show(type)}`)
  }
}

// function reqForm(val) {return a.reqInst(val, Form)}
function isField(val) {return a.isInst(val, GenericField)}
export function reqField(val) {return a.reqInst(val, GenericField)}
export function reqFieldName(val) {return a.reqKey(reqField(val).spec.name)}
function fieldQueryPatch(val) {return reqField(val).getQueryPatch()}
function fieldError(val) {return reqField(val).error}
function fieldClear(val) {reqField(val).clear()}
function fieldUntouch(val) {reqField(val).untouch()}
function fieldValueArr(val) {return uc.trimList(val.getValueMulti())}
function fieldHasValue(val) {return reqField(val).hasValue()}
function fieldValueSingle(val) {return reqField(val).getValueSingle()}
function fieldGroupTitle(val) {return reqField(val).spec.groupTitle}
function isFieldEnabled(val) {return !reqField(val).disabled}
function isFieldTouched(val) {return reqField(val).touched}
function isFilledRatingField(val) {return isFieldStarRating(val) && val.hasValue()}

export class GenericField extends a.Emp {
  constructor(form, spec) {
    super()

    a.priv(this, `form`, a.reqInst(form, Form))
    a.priv(this, `spec`, reqFieldSpec(spec))

    this.values = this.entityValueAnyToInternalValueMulti(spec.value ?? spec.inputProps?.value)
    this.untouch()
    this.enable()

    // TODO gain access to context and use `ctx.seq.nextId`.
    this.id = spec.inputProps?.id ?? ut.rndId()
  }

  // Getter for backwards compatibility. TODO use `.getValueAny` and remove this.
  get value() {return this.getValueAny()}

  getValueAny() {return this.isMultiple() ? this.getValueMulti() : this.getValueSingle()}
  getValueSingle() {return a.head(this.values)}
  getValueMulti() {return this.values}

  setValueAny(val) {return this.setValueMulti(uc.alwaysList(val))}
  setValueSingle(val) {return this.setValueMulti(uc.toListOpt(val))}

  // The return value indicates whether the value is changed.
  // Nil output means no change.
  setValueMulti(val) {
    a.optArr(val)
    if (e.equal(this.values, val)) return undefined
    this.values = val
    this.touch()
    this.validate()
    return this
  }

  resetValueAny(val) {this.resetValueMulti(uc.alwaysList(val))}
  resetValueSingle(val) {this.resetValueMulti(uc.toListOpt(val))}

  resetValueMulti(val) {
    this.values = a.optArr(val)
    this.untouch()
  }

  /*
  The default behavior is to return a dict with exactly one field, corresponding
  to `.spec.name`. However, subclasses may override this to generate multiple
  entity fields.
  */
  async getEntityPatch(ctx) {return this.makePatch(await this.getEntityValueAny(ctx))}

  async getEntityValueAny(ctx) {
    return this.isMultiple() ? this.getEntityValueMulti(ctx) : this.getEntityValueSingle(ctx)
  }

  async getEntityValueSingle() {
    return a.vac(a.hasLen(this.values)) && this.entityValueFromInternalValue(this.getValueSingle())
  }

  async getEntityValueMulti() {
    const src = fieldValueArr(this)
    return a.vac(src) && Promise.all(src.map(this.entityValueFromInternalValue, this))
  }

  resetFromEntity(src) {this.resetValueMulti(this.entityToInternalValueMulti(src))}
  entityToValueAny(src) {return a.laxStruct(src)[reqFieldName(this)]}
  entityToInternalValueMulti(src) {return this.entityValueAnyToInternalValueMulti(this.entityToValueAny(src))}

  // Subclasses may override this for custom decoding/encoding.
  entityValueToInternalValue(val) {return val}
  entityValueFromInternalValue(val) {return val}

  entityValueAnyToInternalValueMulti(val) {
    if (this.isMultiple()) {
      if (a.isNil(val)) return val
      if (a.isArr(val)) return val.map(this.entityValueToInternalValue, this)
      throw TypeError(`type mismatch: field ${a.show(reqFieldName(this))} expects list of values, got single value ${a.show(val)}`)
    }
    return [this.entityValueToInternalValue(val)]
  }

  getQueryValue() {return fieldValueArr(this)?.map(this.queryValueFromInternalValue, this)}
  getQueryPatch() {return this.makePatch(this.getQueryValue())}

  resetFromUrl(src) {this.resetValueMulti(this.urlToInternalValueMulti(src))}
  urlToQueryValueMulti(src) {return ut.toUrl(src).query.getAll(reqFieldName(this))}
  urlToInternalValueMulti(src) {return this.queryValueMultiToInternalValueMulti(this.urlToQueryValueMulti(src))}
  queryValueMultiToInternalValueMulti(src) {return a.arr(src).map(this.queryValueToInternalValue, this)}

  // Subclasses may override this for custom decoding/encoding.
  queryValueToInternalValue(val) {return ut.maybeJsonDecode(val)}
  queryValueFromInternalValue(val) {return ut.maybeStringEncode(val)}

  getDomValueSingle() {return um.normNil(this.domValueFromInternalValue(this.getValueSingle()))}
  setDomValueSingle(val) {return this.setValueSingle(this.domValueToInternalValue(val))}

  // Subclasses may override this for custom decoding/encoding.
  domValueToInternalValue(val) {return val}
  domValueFromInternalValue(val) {return val}

  len() {return a.len(this.values)}
  hasLen() {return !!this.len()}
  hasValue() {return um.isMeaningful(this.getValueSingle())}
  getTitle() {return this.spec.title || this.spec.name}

  normVal(val) {return this.disabled ? null : um.normNil(val)}
  isMultiple() {return !!(this.spec.multiple ?? this.spec.inputProps?.multiple)}
  clear() {return this.setValueMulti()}
  trigger() {this.form.onFieldChange(this)}
  onlyEnabled() {return a.vac(!this.disabled) && this}
  placeholder() {return this.spec.placeholder || this.spec.inputProps?.placeholder}

  /*
  Use caution when overriding this method. This current implementation takes
  care to normalize nil to `null` to ensure proper JSON encoding for "PATCH"
  endpoints, and nullifies values when the field is disabled. Subclasses must
  respect this functionality.
  */
  makePatch(val) {return {[reqFieldName(this)]: this.normVal(val)}}

  validate() {
    /*
    Tentative: if an error was detected in an untouched field, for example by
    running `form.forceValidate()`, this allows changes in OTHER fields to clear
    that error. If we decide against that behavior, this condition should be
    reverted to just `!this.touched`.
    */
    if (!this.touched && !this.error) return
    this.forceValidate()
  }

  forceValidate() {
    const {spec: {validators}} = this
    const validate = fun => fun(this)
    this.error = a.procure(validators, validate)
  }

  enable() {this.disabled = false}
  disable() {this.disabled = true}

  touch() {this.touched = true}
  untouch() {this.touched = false, this.error = undefined}

  /*
  Updates the field based on the state of other fields. Should be used with
  EXTREME CARE: it's easy to accidentally create an infinite loop. We try to
  detect and prevent them, but this also prevents the values from being updated
  at all.
  */
  recompute() {this.spec.recompute?.(this)}

  /*
  We support `.selectables` as an optional override. It may be modified during
  recomputation, while `.spec.inputProps?.selectables` must never be modified.
  */
  getSelectables() {return this.selectables || this.specSelectables()}
  specSelectables() {return this.spec.selectables || this.spec.inputProps?.selectables}

  reqMultiple() {
    if (!(this.spec.multiple ?? true)) {
      throw Error(`field type ${a.show(this.constructor)} requires "multiple"`)
    }
  }

  reqNotMultiple() {
    if (this.spec.multiple) {
      throw Error(`field type ${a.show(this.constructor)} doesn't support "multiple"`)
    }
  }

  // TODO use or remove.
  reqValidValue(val) {
    if (this.isMultiple()) {
      if (a.isNil(val) || a.isArr(val)) return val
      throw Error(`internal error: non-array value for multiple field ${a.show(this.spec.name)}`)
    }
    if (!a.isArr(val)) return val
    throw TypeError(`internal error: array value for non-multiple field ${a.show(this.spec.name)}`)
  }

  linkError() {return {error: this.error}}

  // For `misc.mjs`→`FormField`.
  linkFormField() {
    return {
      label: this.getTitle(),
      error: this.error,
    }
  }

  linkInput() {
    return {
      id: this.id,
      name: this.spec.name,
      value: this.getDomValueSingle(),
      error: this.error,
    }
  }

  /*
  For `m.Select` and other select-style view components.

  Note: `.selectables` is an optional override used with field recomputation,
  particularly for service forms. It may be modified during recomputation,
  while `.spec.inputProps?.selectables` must never be modified.
  */
  linkSelect() {
    return {
      value: this.getValueAny(),
      selectables: this.getSelectables(),
      multiple: this.isMultiple(),
      onValueChange: value => {this.setValueAny(value)?.trigger()},
    }
  }

  // For `m.Multiselect`.
  linkMultiselect() {
    this.reqMultiple()
    return {
      selectedValues: this.getValueMulti(),
      selectables: this.getSelectables(),
      onSelectedValuesChange: value => {this.setValueMulti(value)?.trigger()},
    }
  }

  // For `m.DropdownInput`.
  linkDropdown() {
    return {
      ...this.linkInput(),
      onValueChange: value => {
        this.setValueAny(value)?.trigger()
        this.spec.inputProps.onValueChange?.(value)
      },
    }
  }

  scrollTo() {ud.scrollYToId(this.id)}
  scrollToVisible() {ud.scrollYToVisible(this.id)}
}

class GenericTextField extends GenericField {
  // Skip special query decoding and encoding.
  queryValueToInternalValue(val) {return val}
  queryValueFromInternalValue(val) {return val}

  linkInput() {
    return {
      ...super.linkInput(),
      placeholder: this.placeholder(),
      readOnly: this.disabled,
      onInput: eve => {this.setDomValueSingle(eve.target.value)?.trigger()},
    }
  }
}

class TextField extends GenericTextField {
  linkInput() {return {...super.linkInput(), type: `text`}}
}

class TextareaField extends GenericTextField {
  linkInput() {return {...super.linkInput(), type: `textarea`}}
}

class EmailField extends GenericTextField {
  linkInput() {return {...super.linkInput(), type: `email`}}
}

class PasswordField extends GenericTextField {
  linkInput() {return {...super.linkInput(), type: `password`}}
}

class PhoneField extends GenericTextField {
  queryValueToInternalValue(val) {return ut.trimPhone(val)}
  domValueToInternalValue(val) {return ut.trimPhone(val)}

  linkInput() {
    return {
      ...super.linkInput(),
      type: `tel`,
      onInput: eve => {
        if (this.setDomValueSingle(eve.target.value)) {
          ud.resetInputValue(eve.target, this.getValueSingle())
          this.trigger()
        }
      },
    }
  }
}

/*
We implement numeric inputs as text inputs to avoid the default browser behavior
of changing the value when scrolling or pressing up/down keys.
*/
const pseudoNumericInputProps = {
  type: `text`,
  inputmode: `numeric`,
  pattern: `\\d+`,
}

class NumberField extends GenericTextField {
  queryValueToInternalValue(val) {return ut.parseNum(val)}
  domValueToInternalValue(val) {return ut.parseNum(ut.trimNumber(val))}

  linkInput() {
    return {
      ...super.linkInput(),
      ...pseudoNumericInputProps,
    }
  }
}

class CentsField extends NumberField {
  domValueToInternalValue(val) {return uma.moneyUnitsToCents(super.domValueToInternalValue(val))}
  domValueFromInternalValue(val) {return uma.moneyCentsToUnits(val)}
}

class BoolField extends GenericField {
  queryValueToInternalValue(val) {return ut.parseBool(val)}

  // For internal use by other methods.
  linkCheckboxBase() {
    return {
      id: this.id,
      name: this.spec.name,
      checked: this.getDomValueSingle(),
      disabled: this.disabled,
      onChange: eve => {return this.setValueAny(eve.target.checked)?.trigger()},
    }
  }

  linkCustomCheckbox() {
    return {
      ...this.linkCheckboxBase(),
      error: this.error,
    }
  }

  linkDomCheckbox() {
    return {
      ...this.linkCheckboxBase(),
      type: `checkbox`,
      value: null,
    }
  }

  getSelectables() {
    return super.getSelectables() || (
      this.spec.validation?.required
        ? c.RADIO_BOOL_SELECTABLES
        : c.RADIO_BOOL_SELECTABLES_OPT
    )
  }
}

export class FileFieldValue extends a.Emp {
  constructor(key, file) {
    super()
    this.key = a.optValidStr(key)
    this.setFile(file)
    this.validate()
  }

  pk() {return this.key}
  validate() {if (!this.key && !this.file) throw Error(`invalid file field: no primary key and no file`)}
  hasDomFile() {return !!this.getDomFile()}
  getDomFile() {return a.only(this.file, a.isFile)}
  reqDomFile() {return this.getDomFile() || a.panic(Error(`missing DOM file in file field`))}
  fileFormData() {return ud.fileFormData(this.reqDomFile())}
  guessName() {return this.file?.name || this.pk()}
  sitePath() {return c.getFileDownloadUrl(this.pk())}

  setFile(val) {
    this.file = a.optStruct(val)
    this.setPreviewUrl()
  }

  /*
  Should be used for non-image files and as a fallback for unsupported image
  formats.
  */
  getPreviewText() {
    const {file} = this

    return (
      a.isFile(file)
      ? file.name
      : isDbFile(file)
      ? file.originalName || file.name
      : undefined
    ) || this.key
  }

  /*
  We cache URLs generated via `URL.createObjectURL` because they need to
  be manually revoked via `URL.revokeObjectURL`. This is a placeholder.
  TODO actually revoke.
  */
  getPreviewUrl() {return this.previewUrl ||= this.makePreviewUrl()}

  makePreviewUrl() {
    const {file} = this
    if (a.isFile(file)) return ud.createObjectUrl(file)
    if (isDbFile(file)) return ut.imageUrl(file, c.IMAGE_WIDTH_MEDIUM)?.toString()
    return undefined
  }

  setPreviewUrl(val) {
    a.optValidStr(val)
    if (this.previewUrl !== val) ud.revokeObjectUrl(this.previewUrl)
    this.previewUrl = val
  }

  // Must be compatible with `isTransformableImageSource`.
  getTransformable() {return this.getDomFile() || this.getPreviewUrl()}
  isTransformable() {return !!this.getTransformable()}

  deinit() {ud.revokeObjectUrl(this.previewUrl)}

  static fromFile(val) {return new this(um.pkOpt(a.reqStruct(val)), val)}

  // Questionable name, implies making 1 instance rather than list.
  static fromFiles(val) {return a.arr(val).map(this.fromFile, this)}
}

function getPreviewUrl(val) {return val.getPreviewUrl()}

/*
Pending files are stored in `.files`, as instances of `FileFieldValue`.
The "actual" field value is a list of ids, and is not updated when adding or
removing files, because newly added files don't have DB ids. When committing a
form, we have an intermediary step where we upload files to generate a list of
ids, which becomes the actual value of the field.
*/
export class FileField extends GenericField {
  constructor(form, spec) {
    super(form, spec)

    const index = a.index(uc.alwaysList(spec.related), um.getId)
    const init = key => new this.Value(key, index[key])

    /*
    TODO consider renaming this property. The name "file" is misleading, because
    this actually stores wrapper instances that combine primary key and DB
    entity or DOM blob.
    */
    this.files = a.mapCompact(a.compact(uc.alwaysList(spec.value)), init)
  }

  get Value() {return FileFieldValue}

  async getEntityValueSingle(ctx) {
    return a.head(await this.getEntityValueMulti(ctx))
  }

  async getEntityValueMulti(ctx) {
    await this.commit(ctx)
    return this.values
  }

  getFile() {return a.head(this.files)}
  getFiles() {return this.files}
  addFiles(val) {return this.spec.multiple ? this.appendFiles(val) : this.replaceFiles(val)}
  replaceFiles(val) {return this.files = this.Value.fromFiles(val), this.touch(), this}
  appendFiles(val) {return this.files.push(...this.Value.fromFiles(val)), this.touch(), this}

  len() {return a.len(this.files)}
  hasValue() {return a.hasLen(this.files)}
  getPreviewUrl() {return this.getFile()?.getPreviewUrl()}
  getPreviewUrls() {return a.mapCompact(this.getFiles(), getPreviewUrl)}
  mediaType() {}

  onDrop(eve) {
    a.eventKill(eve)
    const files = eve.dataTransfer?.files
    if (files) this.addFiles(files)?.trigger()
  }

  onChange(eve) {
    a.eventKill(eve)
    this.addFiles(eve.target.files)

    // Prevents various gotchas.
    eve.target.value = null

    this.trigger()
  }

  linkLabel() {
    return {
      onDragOver: a.eventKill,
      onDrop: this.onDrop.bind(this),
    }
  }

  linkInput() {
    return {
      id: this.id,
      name: this.spec.name,
      type: `file`,
      class: `invis`,
      accept: this.mediaType(),
      multiple: this.isMultiple(),
      onChange: this.onChange.bind(this),
      disabled: this.disabled,
    }
  }

  linkClear() {return {onClick: eve => {a.eventKill(eve), this.clear()}}}

  linkRemoveFile(val) {
    return {
      onClick: eve => {
        a.eventKill(eve)
        this.removeFile(val)?.trigger()
      }
    }
  }

  removeFile(val) {return uc.removedMut(this.files, val) && (this.touch(), this)}

  linkEditFile(ctx, src) {
    return a.vac(src) && {
      onClick: async eve => {
        a.eventKill(eve)
        if (await this.editedFile(ctx, src)) this.trigger()
      }
    }
  }

  // TODO general approach to displaying errors that may occur in field editing.
  // This case is particularly error-prone because it involves a big 3rd party
  // library.
  async editedFile(ctx, src) {
    try {
      return await this.editFile(ctx, src)
    }
    catch (err) {
      um.onUnhandledErr(err)
      return false
    }
  }

  async editFile(ctx, src) {
    a.reqInst(src, this.Value)

    const file = await ctx.env.filestack.transform(src.getTransformable())
    if (!file) return false

    src.setFile(file)
    this.touch()
    return true
  }

  async commit(ctx) {
    const [domFiles, dbFiles] = a.partition(this.files, hasDomFile)
    this.files = a.concat(dbFiles, this.Value.fromFiles(await this.uploadMultiple(ctx, domFiles)))
    this.setValueAny(um.pksOpt(this.files))
  }

  async uploadMultiple(ctx, vals) {
    const upload = val => this.upload(ctx, val)
    return um.PromiseList.map(vals, upload)
  }

  // Override in subclass.
  upload(ctx, val) {return n.createFile(ctx, fileFormData(val))}
}

function isDbFile(val) {return a.isStruct(val) && !!val.id && !!val.name}
// function reqDbFile(val) {return a.req(val, isDbFile)}
function hasDomFile(val) {return reqFileFieldValue(val).hasDomFile()}
function reqFileFieldValue(val) {return a.reqInst(val, FileFieldValue)}
function fileFormData(val) {return reqFileFieldValue(val).fileFormData()}

class ImageFileField extends FileField {
  mediaType() {return `image/*`}
  upload(ctx, val) {return n.createImageFile(ctx, fileFormData(val))}
}

class BookFileField extends FileField {
  mediaType() {return ut.extsToFileInputAccept(c.BOOK_EXTS)}
  upload(ctx, val) {return n.createBookFile(ctx, fileFormData(val))}
}

class VidFieldValue extends FileFieldValue {
  makePreviewUrl() {return super.makePreviewUrl() || ut.vimeoEmbedUrl({id: this.key})}
  upload() {throw Error(`vid upload must be implemented at the level of the view component`)}
}

/*
Note: while `FileField` by default represents our table `files` where blobs are
uploaded to our backend, this class represents our table `vids` where blobs are
uploaded to Vimeo.
*/
class VidField extends FileField {
  constructor(...src) {
    super(...src)
    this.reqNotMultiple()
  }

  getEntityValueAny() {throw Error(`vid upload must be implemented at the level of the view component`)}

  get Value() {return VidFieldValue}
}

class DateField extends GenericField {
  entityValueToInternalValue(val) {return uti.toValidDateOpt(val)}
  queryValueToInternalValue(val) {return uti.toValidDate(val)}
  queryValueFromInternalValue(val) {return uti.toTimestamp(val)}
  domValueToInternalValue(val) {return uti.toValidDate(val)}
  domValueFromInternalValue(val) {return a.vac(ut.formatDateIsoWithoutTime(val))}
  setValueAny(val) {return super.setValueAny(a.optValidDate(val))}

  linkInput() {
    return {
      ...super.linkInput(),
      type: `date`,
      disabled: this.disabled,
      onChange: event => {this.setDomValueSingle(event.target.value)?.trigger()},
    }
  }

  linkCustomInput() {
    return {
      type: `date`,
      value: this.getValueSingle(),
      disabled: this.disabled,
      onChange: value => {this.setValueAny(value)?.trigger()},
    }
  }
}

class DateTimeField extends DateField {
  getQueryValue() {return this.queryValueFromInternalValue(this.getValueSingle())}
  queryValueFromInternalValue(val) {return uti.toValidDateOpt(val)?.toISOString()}
  domValueFromInternalValue(val) {return a.vac(ut.formatDateIsoWithTimeTimezoneOffset(val))}

  linkInput() {
    return {
      ...super.linkInput(),
      type: `datetime-local`, // WTF type
    }
  }
}

// TODO rename `coordName` → `pointName`, `coordValue` → `pointValue`.
class GeoField extends GenericTextField {
  constructor(form, spec) {
    super(form, spec)
    this.reqNotMultiple()
    reqCoordName(this)
    this.setRelFieldValue(spec.coordValue)
  }

  queryFormatCoordValue(_value) {throw ue.errImplementInSubclass()}
  optRelFieldValueValidator(_value) {throw ue.errImplementInSubclass()}
  queryDecodeRelFieldValue(value) {return value}

  setPair(name, point) {
    if (name === this.getValueSingle() && e.equal(point, this.relFieldValue)) {
      return undefined
    }
    this.setRelFieldValue(point)
    this.setValueSingle(name)
    return this
  }

  setRelFieldValue(val) {this.relFieldValue = this.optRelFieldValueValidator(val)}
  entityToPoint(src) {return a.laxStruct(src)[reqCoordName(this)]}
  urlToPoint(src) {return this.queryDecodeRelFieldValue(ut.toUrl(src).query.get(reqCoordName(this)))}
  clear() {return this.setPair()}

  getEntityPatch() {
    return {
      [reqFieldName(this)]: this.normVal(this.getValueSingle()),
      [reqCoordName(this)]: this.normVal(this.relFieldValue),
    }
  }

  resetFromEntity(src) {
    super.resetFromEntity(src)
    this.setRelFieldValue(this.entityToPoint(src))
  }

  getQueryPatch() {
    const val = this.getValueSingle()
    return {
      [reqFieldName(this)]: val,
      [reqCoordName(this)]: a.vac(val) && this.queryFormatCoordValue(this.relFieldValue),
    }
  }

  resetFromUrl(src) {
    super.resetFromUrl(src)
    this.setRelFieldValue(this.urlToPoint(src))
  }

  // Should be used in combination with `.linkGooglePlacesAutocomplete`.
  // See existing examples.
  linkInput() {
    return {
      ...super.linkInput(),
      onInput: eve => {
        if (!eve.target.value) this.clear()?.trigger()
      },
      onBlur: event => {
        if (um.isGeoPoint(this.relFieldValue)) {
          ud.resetInputValue(event.target, this.getValueSingle())
          return
        }
        this.setPair(event.target.value)?.trigger()
      },
    }
  }
}

class GeoFieldGooglePlacesLinkable extends GeoField {
  getFieldValueFromGooglePlace(_place) {throw ue.errImplementInSubclass()}

  linkGooglePlacesAutocomplete() {
    return {
      onPlaceChange: place => {
        this.setPair(place.formatted_address, this.getFieldValueFromGooglePlace(place))?.trigger()
      },
    }
  }
}

class GeoFieldPoint extends GeoFieldGooglePlacesLinkable {
  optRelFieldValueValidator(value) {return um.optGeoPoint(value)}
  hasPoint() {return um.isGeoPoint(this.relFieldValue)}
  queryFormatCoordValue(val) {a.jsonEncode(val)}
  getFieldValueFromGooglePlace(place) {return um.googlePlaceToGeoPoint(place)}
  queryDecodeRelFieldValue(value) {return a.jsonDecode(value)}
}

class GeoFieldGooglePlaceId extends GeoFieldGooglePlacesLinkable {
  optRelFieldValueValidator(value) {return a.optStr(value)}
  getFieldValueFromGooglePlace(place) {return um.getPlaceId(place)}
  queryFormatCoordValue(val) {return val}
}

function reqCoordName(val) {
  return (
    a.onlyKey(val.spec.coordName) ||
    a.panic(Error(`geo field requires a valid "coordName"`))
  )
}

/*
Currently doesn't need any special behaviors, the select functionality is built
into generic fields.
*/
class SelectField extends GenericField {}

class StarRatingField extends NumberField {
  linkStarRating() {
    return {
      ...super.linkInput(),
      onValueChange: val => {this.setValueSingle(val)?.trigger()},
    }
  }
}

class RatePentaField extends StarRatingField {
  getSelectables() {return super.getSelectables() || a.times(5, numSelInc)}
}

class RateDecField extends StarRatingField {
  getSelectables() {return super.getSelectables() || a.times(10, numSelInc)}
}

export function reqFormSpec(val) {
  a.reqStruct(val)
  optFieldSpecs(val.fieldSpecs)
  return val
}

export function optFieldSpecs(val) {
  a.optArrOf(val.fieldSpecs, isFieldSpec)
  return val
}

/*
Note: field specs are allowed to have additional properties that will not be
listed here.
*/
function isFieldSpec(val) {
  return a.isStruct(val) &&
    a.isValidStr(val.name) &&
    a.isValidStr(val.type) &&
    a.every(val.validators, a.isFun)
}

function reqFieldSpec(val) {return a.req(val, isFieldSpec)}
// function reqFieldSpecs(val) {return a.reqArrOf(val, isFieldSpec)}

/*
Unused for now.

function reqFieldSpecGroup(val) {
  reqFieldSpec(val)

  if (!a.optStr(val.groupTitle)) {
    throw Error(`expected field spec ${a.show(val.name)} to have group title`)
  }
}
*/

export function isFieldTypeText(type) {
  return (
    type === c.UI_FIELD_TYPE_TEXT     ||
    type === c.UI_FIELD_TYPE_TEXTAREA ||
    type === c.UI_FIELD_TYPE_SEARCH   ||
    type === c.UI_FIELD_TYPE_EMAIL    ||
    type === c.UI_FIELD_TYPE_PHONE    ||
    type === c.UI_FIELD_TYPE_NUMBER   ||
    type === c.UI_FIELD_TYPE_CENTS
  )
}

function isFieldTypeSearch(val) {return val === c.UI_FIELD_TYPE_SEARCH}
function isFieldSpecSearch(val) {return isFieldTypeSearch(val.type)}

export function isFieldStarRating(val) {
  return isField(val) && (
    val.spec.type === c.UI_FIELD_TYPE_STAR_RATING ||
    val.spec.type === c.UI_FIELD_TYPE_RATE_PENTA ||
    val.spec.type === c.UI_FIELD_TYPE_RATE_DEC
  )
}

// export function serverFormSpecToLegacyFormSpec(src) {
//   return serverFieldSpecsToLegacyFormSpec(a.optStruct(src)?.fields)
// }

export function serverFieldSpecsToLegacyFormSpec(src) {
  return {fieldSpecs: serverFieldSpecsToLegacyFieldSpecs(src)}
}

export function serverFieldSpecsToLegacyFieldSpecs(src) {
  return a.compact(a.arr(src).map(serverFieldSpecToLegacyFieldSpec))
}

/*
Adapts the server type `UiField` to our own field format. The plan is to fully
migrate to server-based specs and remove this adaptation.

Missing features:

  * `.validation`
  * `.selectableEndpoint`
*/
function serverFieldSpecToLegacyFieldSpec({
  name,
  type,
  multiple,
  placeholder,
  selectables,
  selectableEndpoint,
  value,
  groupTitle,
  ...spec
}, _key, specs) {
  if (type === c.UI_FIELD_TYPE_GEO_POINT || type === c.UI_FIELD_TYPE_GOOGLE_PLACE_ID) return undefined

  if (type === c.UI_FIELD_TYPE_GEO_NAME) {
    const pointSpec = a.find(specs, val => val.type === c.UI_FIELD_TYPE_GEO_POINT && val.groupTitle === groupTitle)
    if (!pointSpec) {
      throw Error(`failed to find corresponding "geoPoint" spec for "geoName" spec ${a.show(name)}`)
    }
    spec.coordName = pointSpec.name
  }

  if (type === c.UI_FIELD_TYPE_GOOGLE_PLACE_NAME) {
    const pointSpec = a.find(specs, val => val.type === c.UI_FIELD_TYPE_GOOGLE_PLACE_ID && val.groupTitle === groupTitle)
    if (!pointSpec) {
      throw Error(`failed to find corresponding "googlePlaceId" spec for "googlePlaceName" spec ${a.show(name)}`)
    }
    spec.coordName = pointSpec.name
  }

  const endpoint = dropdownInputProps(selectableEndpoint)

  // Only `m.DropdownInput` supports dynamic fetching.
  if (type === c.UI_FIELD_TYPE_SELECT && endpoint) type = c.UI_FIELD_TYPE_DROPDOWN

  return uc.trimDict({
    ...spec,
    name,
    type,
    inputProps: {
      multiple,
      placeholder,
      selectables,
      value,
      ...dropdownInputProps(selectableEndpoint),
    },
  })
}

// Converts `UiSelectableEndpoint` as defined by the server into data-fething
// props supported by `m.DropdownInput`.
function dropdownInputProps(val) {
  if (!val) return undefined

  const {type, path, titleKey, valueKey, filterKey, filters} = a.laxDict(val)

  if (!type || !path || !titleKey || !valueKey) {
    if (c.PUBLIC_RELEASE) return undefined
    throw Error(`invalid endpoint spec ${a.show(val)}`)
  }

  const searchKey = a.find(filters, isFieldSpecSearch)?.name || c.SEARCH_TEXT_KEY

  async function fetchItems(ctx, query) {
    const body = await (await a.resOk(ctx.fetch({
      url: ut.url(path).queryPatch(query),
      headers: [a.HEADER_JSON_ACCEPT],
    }))).json()
    if (type === `page`) return a.laxDict(body).items
    return body
  }

  // See `m.DropdownInput`.
  const fetchItem = !filterKey ? undefined : async function fetchItem(ctx, {value}) {
    const val = a.head(await fetchItems(ctx, {[filterKey]: value}))
    if (!val) {
      throw new ue.ErrNotFound(`failed to fetch by key ${value}`)
    }
    return val
  }

  return {
    fetchItem,
    fetchItems: (ctx, {searchText}) => fetchItems(ctx, {[searchKey]: searchText}),
    getItemKey(item) {
      return a.laxStruct(item)[valueKey]
    },
    getItemText(item) {
      return a.laxStruct(item)[titleKey]
    },
  }
}

function numSel(value) {return {title: value, value}}
function numSelInc(value) {return numSel(value + 1)}

export function fieldSpecHidden(name, value) {
  return {
    name: a.reqValidStr(name),
    type: c.UI_FIELD_TYPE_HIDDEN,
    value,
  }
}
